import { useState } from "react";
import { jsPDF } from "jspdf";

import Api from "../../../shared/api/api";
import WarningMsg from "./WarningMsg";

import "../styles/CreateEmail.css";

const RANCH_HORSE_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSfn6-HiveTDAJrPfuhhxEPd2GmGJ_1fQh3aQs2I1vgDWZTt9A/viewform?usp=header";

const DUE_DATE = "July 9th";
const TEST_YEAR = "2026";
const TEST_TITLE = `${TEST_YEAR} 4H Knowledge Test`;

const removeDataUrlPrefix = (value = "") =>
  value.includes("base64,")
    ? value.split("base64,")[1]
    : value;

const buildFullName = (user) =>
  [
    user?.name_first,
    user?.name_middle,
    user?.name_last,
  ]
    .filter(Boolean)
    .join(" ");

export default function CreateEmail({
  userId,
  testId,
  users = [],
  onSent,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const user = users.find(
    (candidate) =>
      candidate.four_h_id === userId
  );

  const leader = user
    ? users.find(
        (candidate) =>
          candidate.team === user.team &&
          Boolean(candidate.leader)
      )
    : null;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!user) {
      setError(
        "The participant could not be found."
      );
      return;
    }

    if (!leader?.email) {
      setError(
        "A team leader email address could not be found."
      );
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const pdfData = await Api.getPdf(
        userId,
        testId
      );

      if (
        pdfData?.error ||
        !Array.isArray(pdfData) ||
        !pdfData[0]?.page_one ||
        !pdfData[0]?.page_two
      ) {
        throw new Error(
          pdfData?.error ||
            "The assessment PDF could not be retrieved."
        );
      }

      const pdfDocument = new jsPDF();

      const pageWidth =
        pdfDocument.internal.pageSize.getWidth();

      const pageHeight =
        pdfDocument.internal.pageSize.getHeight();

      const pageOne =
        removeDataUrlPrefix(pdfData[0].page_one);

      const pageTwo =
        removeDataUrlPrefix(pdfData[0].page_two);

      pdfDocument.addImage(
        `data:image/jpeg;base64,${pageOne}`,
        "JPEG",
        0,
        0,
        pageWidth,
        pageHeight
      );

      pdfDocument.addPage();

      pdfDocument.addImage(
        `data:image/jpeg;base64,${pageTwo}`,
        "JPEG",
        0,
        0,
        pageWidth,
        pageHeight
      );

      const pdfBase64 = pdfDocument
        .output("datauristring")
        .split(",")[1];

      const participantName =
        buildFullName(user);

      const recipients = [
        leader.email,
        user.email,
      ].filter(Boolean);

      const message = `Hello,

Attached are the assessment results for ${participantName}.

The report highlights answers the participant identified correctly and incorrectly. Red highlighting means the submitted answer was incorrect. Green highlighting means the answer was correct. An unhighlighted multiple-choice answer indicates that the participant did not answer the question.

In the identification section, the participant's submitted answer is displayed. Correct answers are highlighted green. Incorrect or unanswered items are highlighted red.

You have until noon on ${DUE_DATE} to submit a Ranch Horse Test Discrepancy form at the following link:

${RANCH_HORSE_FORM_URL}

Thank you.`;

      const emailResponse = await Api.sendEmail({
        to: recipients.join(", "),
        subject: `${TEST_TITLE} for ${participantName}`,
        body: message,
        attachments: [
          {
            filename: `User-${participantName}-Test-${TEST_TITLE}.pdf`,
            content: pdfBase64,
            contentType: "application/pdf",
          },
        ],
      });

      if (emailResponse?.error) {
        throw new Error(emailResponse.error);
      }

      const updateResponse =
        await Api.updateUserAssessment(
          userId,
          testId,
          {
            sent: true,
          }
        );

      if (updateResponse?.error) {
        throw new Error(updateResponse.error);
      }

      if (typeof onSent === "function") {
        onSent();
      }
    } catch (requestError) {
      console.error(
        "Unable to send assessment email:",
        requestError
      );

      setError(
        requestError?.message ||
          "The email could not be sent."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!user || !leader) {
    return null;
  }

  return (
    <article className="readyToEmailDiv">
      {isLoading && (
        <WarningMsg message="The email is being prepared and sent." />
      )}

      {error && (
        <p className="fourHFormStatus error" role="alert">
          {error}
        </p>
      )}

      <p>
        <strong>User ID:</strong> {userId}
      </p>

      <p>
        <strong>Test ID:</strong> {testId}
      </p>

      <p>
        <strong>Team Name:</strong>{" "}
        {user.team || "Not assigned"}
      </p>

      <p>
        <strong>Team Leader Name:</strong>{" "}
        {buildFullName(leader)}
      </p>

      <p>
        <strong>Team Email:</strong>{" "}
        {leader.email}
      </p>

      <form
        onSubmit={handleSubmit}
        className="submitUserUpdateForm"
      >
        <button
          className="sendEmailBtn"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? "Sending..." : "Send Email"}
        </button>
      </form>
    </article>
  );
}